import React from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

/**
 * Wizard Component - Multi-step form container
 * @param {Object} props
 * @param {number} props.currentStep - Current active step (0-indexed)
 * @param {Function} props.setCurrentStep - Function to change current step
 * @param {Array} props.steps - Array of step objects with { id, title, description }
 * @param {React.ReactNode} props.children - Step content to render
 * @param {Function} props.onNext - Function called when clicking next
 * @param {Function} props.onPrevious - Function called when clicking previous
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {boolean} props.showMobileNext - Whether to show Next button in footer on mobile (default: false)
 */
const Wizard = ({
  currentStep,
  setCurrentStep,
  steps,
  children,
  onNext,
  onPrevious,
  canGoNext = true,
  isSubmitting = false,
  showMobileNext = false,
}) => {
  const totalSteps = steps.length;

  const handleNext = () => {
    if (currentStep < totalSteps - 1 && canGoNext) {
      if (onNext) {
        onNext();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      if (onPrevious) {
        onPrevious();
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < totalSteps && stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  return (
    <div className="w-full">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const isClickable = index <= currentStep;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <button
                    type="button"
                    onClick={() => goToStep(index)}
                    disabled={!isClickable || isSubmitting}
                    className={`
                      relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                      transition-all duration-300
                      ${isCompleted
                        ? 'bg-[#27489E] text-white shadow-lg scale-110'
                        : isActive
                          ? 'bg-[#27489E] text-white shadow-lg scale-110 ring-4 ring-blue-100'
                          : 'bg-gray-200 text-gray-500'
                      }
                      ${isClickable && !isSubmitting ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed'}
                    `}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <span className="text-lg font-bold">{index + 1}</span>
                    )}
                  </button>
                  <div className="mt-3 text-center">
                    <p className={`text-sm font-semibold ${isActive ? 'text-[#27489E]' : isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                      {step.title}
                    </p>
                    {step.description && (
                      <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                    )}
                  </div>
                </div>
                {index < totalSteps - 1 && (
                  <div className="flex-1 mx-2 h-1 relative">
                    <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-500 ${
                        isCompleted ? 'bg-[#27489E]' : 'bg-gray-200'
                      }`}
                      style={{ width: isCompleted ? '100%' : '0%' }}
                    ></div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 min-h-[500px]">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {steps[currentStep]?.title}
          </h2>
          {steps[currentStep]?.description && (
            <p className="text-gray-600 mt-2">{steps[currentStep].description}</p>
          )}
        </div>

        <div className="space-y-6">
          {children}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8">
        {/* Previous button: shown on mobile only, hidden on desktop (floating button used instead) */}
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentStep === 0 || isSubmitting}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all md:hidden
            ${currentStep === 0 || isSubmitting
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-[#27489E] hover:text-[#27489E] hover:shadow-md'
            }
          `}
        >
          <ChevronLeft className="w-5 h-5" />
          Précédent
        </button>

        <div className="text-sm text-gray-500">
          Étape {currentStep + 1} sur {totalSteps}
        </div>

        {/* Next button: shown on mobile when showMobileNext is true, hidden on desktop */}
        {showMobileNext && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || isSubmitting || currentStep === totalSteps - 1}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all md:hidden
              ${!canGoNext || isSubmitting || currentStep === totalSteps - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#F5A623] text-white hover:bg-[#e0951f] hover:shadow-lg hover:scale-105'
              }
            `}
          >
            Suivant
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        
        {/* Next button: shown when showMobileNext is false (default behavior) */}
        {!showMobileNext && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || isSubmitting || currentStep === totalSteps - 1}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all
              ${!canGoNext || isSubmitting || currentStep === totalSteps - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#F5A623] text-white hover:bg-[#e0951f] hover:shadow-lg hover:scale-105'
              }
            `}
          >
            Suivant
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Wizard;

